import { useEffect, useState } from "react";
import * as api from "../apis";
import {
  Alert,
  Button,
  Card,
  Container,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import {
  ConversationStatusEnum,
  IConversation,
  MessageFromEnum,
} from "./interfaces";
import LoadingDialog from "./LoadingDialog";

export default function Chat(props: {
  conversationId: string;
  changeCurrentView: Function;
}) {
  const [conversation, setConversation] = useState<IConversation | null>(null);
  const [userReply, setUserReply] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [paymentRequired, setPaymentRequired] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentResultAlert, setPaymentResultAlert] = useState<
    "success" | "failed" | null
  >(null);

  async function getConversation() {
    const result = await api.getConversation(props.conversationId);
    setConversation(result.data.conversation);
    setPaymentRequired(!!result.data.paymentRequired);
    setPaymentAmount(result.data.paymentAmount ?? 0);
  }

  useEffect(() => {
    getConversation();

    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get("payment");
    if (paymentStatus === "success" || paymentStatus === "failed") {
      setPaymentResultAlert(paymentStatus);
      params.delete("payment");
      params.delete("view");
      params.delete("conversationId");
      const newSearch = params.toString();
      window.history.replaceState(
        {},
        "",
        window.location.pathname + (newSearch ? `?${newSearch}` : "")
      );
    }
  }, []);

  async function startPayment() {
    try {
      setLoading(true);
      const result = await api.createPayment(props.conversationId);
      if (result.status === "ok") {
        window.location.href = result.data.redirectUrl;
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Container>
      {paymentResultAlert && (
        <Alert
          severity={paymentResultAlert === "success" ? "success" : "error"}
          onClose={() => setPaymentResultAlert(null)}
          sx={{ marginBottom: 2 }}
        >
          {paymentResultAlert === "success"
            ? "پرداخت با موفقیت انجام شد. اکنون می‌توانید به گفتگو ادامه دهید."
            : "پرداخت ناموفق بود یا لغو شد. لطفا مجددا تلاش کنید."}
        </Alert>
      )}
      {/* <Box sx={{ display: "flex" }}> */}
      {conversation &&
        conversation.userGptMessages.map((message) => (
          <Card
            variant="outlined"
            sx={{
              display: "flex",
              textAlign: "right",
              padding: 2,
              border: "2px solid #000",
              borderRadius: 4,
              margin: 2,
              flexDirection:
                message.from === MessageFromEnum.SYSTEM ? "row" : "row-reverse",
              justifyContent:
                message.from === MessageFromEnum.SYSTEM
                  ? "flex-start"
                  : "flex-end",
            }}
          >
            {/* <Typography>{message.from}</Typography> */}
            <Typography>{message.content}</Typography>
          </Card>
        ))}
      {/* </Box> */}
      {paymentRequired && (
        <Alert severity="warning" sx={{ marginY: 2 }}>
          شما به سقف پیام‌های رایگان در این گفتگو رسیده‌اید. برای ادامه گفتگو
          باید مبلغ {paymentAmount.toLocaleString("fa-IR")} ریال پرداخت کنید.
        </Alert>
      )}
      <Grid container rowSpacing={2}>
        {paymentRequired ? (
          <Grid item xs={12}>
            <Button fullWidth variant="contained" onClick={startPayment}>
              پرداخت و ادامه گفتگو
            </Button>
          </Grid>
        ) : (
          <>
            <Grid item xs={12}>
              <TextField
                placeholder="پاسخ شما..."
                fullWidth
                value={userReply}
                onChange={(e) => setUserReply(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                disabled={
                  conversation?.status !==
                  ConversationStatusEnum.NEEDS_USER_RESPONSE
                }
                onClick={async () => {
                  try {
                    setLoading(true);
                    const result = await api.replyToConversation(
                      conversation?._id!,
                      userReply
                    );
                    if (result.status === "paymentRequired") {
                      await getConversation();
                      return;
                    }
                    setUserReply("");
                    await getConversation();
                  } catch (error) {
                    console.log(error);
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                ارسال
              </Button>
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Button
            fullWidth
            disabled={
              conversation?.status !==
                ConversationStatusEnum.NEEDS_USER_RESPONSE || paymentRequired
            }
            variant="contained"
            onClick={async () => {
              try {
                setLoading(true);
                await api.finishConversation(conversation?._id!);
                setUserReply("");
                await getConversation();
              } catch (error) {
                console.log(error);
              } finally {
                setLoading(false);
              }
            }}
          >
            جواب خود را دریافت کردم
          </Button>
        </Grid>

        <Grid item xs={12}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              props.changeCurrentView("list");
            }}
          >
            بازگشت
          </Button>
        </Grid>
      </Grid>

      {loading && <LoadingDialog />}
    </Container>
  );
}
